// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import { getPathFromUrl } from "@eisinc/App/Utilities/helpers";
import { clipboardText } from "@eisinc/App/Widgets/DesignSystem/Utilities";
import { XProductModel } from "@eisinc/Overrides/Pages/ProductDetailsPage";
import Copy from "@eisinc/XDL/Icons/Copy";
import Print from "@eisinc/XDL/Icons/Print";
import { createLinkIconStyles } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductShareLink, { ProductShareLinkStyles } from "@insite/content-library/Components/ProductShareLink";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});

export interface ProductDetailsTopLinksStyles {
    wrapper?: InjectableCss;
    link?: ProductShareLinkStyles;
    printLink?: LinkPresentationProps;
    copyLink?: LinkPresentationProps;
}

export const shareLinkStyles: ProductDetailsTopLinksStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-content: center;
            gap: 24px;
            margin-top: 0;
        `,
    },
    printLink: {
        ...createLinkIconStyles({
            icon: Print,
        }),
    },
    copyLink: {
        ...createLinkIconStyles({
            icon: Copy,
        }),
    },
};

const styles = shareLinkStyles;

const ProductDetailsTopLinks: React.FC<Props> = ({ product, variantSelectionCompleted }) => {
    // XNG-Change
    const isLoading = (product as XProductModel)?.isLoading;
    const toaster = useToaster();

    if (isLoading) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Skeleton width="235px" height="14px" />
            </StyledWrapper>
        );
    }

    const copyLinkClickHandler = () => {
        let url = getPathFromUrl(window.location.href);

        if (variantSelectionCompleted) {
            url += `?option=${product.productNumber}`;
        }

        const isCopied = clipboardText(url);
        toaster.addToast({
            body: isCopied ? translate("Copied!") : translate("Copy Failed"),
            messageType: isCopied ? "success" : "danger",
        });
    };
    const printLinkClickHandler = () => {
        openPrintDialog();
    };

    if (!product) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Link {...styles.copyLink} onClick={copyLinkClickHandler} data-test-selector="productCopyLink">
                {translate("Copy Page Link")}
            </Link>
            <ProductShareLink extendedStyles={styles.link} />
            <Link {...styles.printLink} onClick={printLinkClickHandler} data-test-selector="productPrintLink">
                {translate("Print")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsTopLinks)),
    definition: {
        displayName: "Product Details Links",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
